/**
 * @file 动态口令（Google 验证码）表单项
 * @description 敏感操作（改密码 / IP 白名单增删改 / 用户与角色的增删改权）统一要求输入
 *              6 位动态口令，随业务接口以 googleCode 参数提交，由后端校验。
 *              前端只做「必填 + 6 位数字」的格式校验，不再单独调 /google/auth/verify 预校验
 */

import { ProFormText } from '@ant-design/pro-components';
import type { ProFormFieldItemProps } from '@ant-design/pro-form/es/typing';
import { useTranslation } from '@/utils/util';

/** 动态口令：6 位数字 */
export const GOOGLE_CODE_REG = /^\d{6}$/;

/** 表单里动态口令字段统一用这个 name，与后端参数名保持一致 */
export const GOOGLE_CODE_FIELD = 'googleCode';

/**
 * 把表单里的字符串口令转成后端要求的 int32
 * @description 后端 googleCode 是 integer(int32)，输入框拿到的是字符串；
 *              空值返回 undefined，避免把 NaN 或空串发给后端
 * @param {number|string} [code] - 表单值
 * @returns {number|undefined} 数字口令
 */
export const toGoogleCode = (code?: number | string) => {
  if (code === undefined || code === null || String(code).trim() === '') {
    return undefined;
  }
  const num = Number(code);
  return Number.isNaN(num) ? undefined : num;
};

interface GoogleCodeFieldProps
  extends Pick<
    ProFormFieldItemProps,
    'label' | 'disabled' | 'formItemProps' | 'placeholder'
  > {
  /** 字段名，默认 googleCode */
  name?: string;
}

/**
 * 动态口令输入项
 * @param props - 组件属性
 * @param props.name - 字段名，默认 googleCode
 * @param props.disabled - 是否禁用
 * @param props.label - 自定义标签，默认「动态口令」
 * @param props.placeholder - 自定义占位符
 * @param props.formItemProps - 透传给 Form.Item 的属性
 */
const GoogleCodeField: React.FC<GoogleCodeFieldProps> = ({
  name = GOOGLE_CODE_FIELD,
  label,
  placeholder,
  disabled,
  formItemProps,
}) => {
  const t = useTranslation();

  return (
    <ProFormText
      name={name}
      label={label ?? t('common.dynamicPassword')}
      placeholder={placeholder ?? t('common.enterDynamicPassword')}
      disabled={disabled}
      fieldProps={{ maxLength: 6, autoComplete: 'off' }}
      formItemProps={formItemProps}
      rules={[
        { required: true, message: t('common.enterDynamicPassword') },
        { pattern: GOOGLE_CODE_REG, message: t('common.dynamicMustBe6') },
      ]}
    />
  );
};

export default GoogleCodeField;
